// Story 4.3 — /vault/shares/:share_id SPA page. The EPIC-4
// SHARE-DETAIL SURFACE.
//
// Closes the placeholder route Story 4.2 left as a 404 + closes the
// Story 3.9 WelcomePage opened-state "View your certificate" CTA target +
// closes the Story 3.6 RevealCeremony Phase-4 default-variant "View in
// Vault" navigation target.
//
// Layout (top → bottom):
//   1. Breadcrumbs (UX-DR26): Vault › [Drop name] › [Creator name] share #N
//   2. Hero <Certificate size="hero" interactive showMeta> — FIRST
//      production consumer of all three flags together.
//   3. OriginLinkFooter — Story 4.4 LIVE component (was Story 4.3
//      placeholder). Golden traceback line + framer-motion exit per
//      UX-DR20 + UX-spec L1584-L1611.
//   4. Meta panel — drop / bought-for / holding-duration / opened-on
//      / current-floor-price (Epic-5 placeholder).
//   5. Action toolbar — Favourite (LIVE Switch — Story 4.4) + Share
//      (LIVE: navigator.share + clipboard) + List for sale (disabled
//      until Epic 5) + Replay reveal ceremony (LIVE: FIRST production
//      <RevealCeremony replayMode> consumer; Story 3.8 scaffolded the
//      prop). "Highlight on profile" is cut for V1 (removed here + in
//      the ceremony toolbar).
//   6. Marketplace activity panel — Epic-5 placeholder copy.
//   7. Current marketplace listings panel — Epic-5 placeholder copy.
//
// Replay flow uses the locally-cached share data — NO useReveal
// mutation (replay does NOT issue a new share; it re-plays the
// existing one).
//
// SEO: noindex via useEffect head-tag injection (no react-helmet
// dependency — matches the codebase's no-extra-dep convention).

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  OriginLinkFooter,
  PatronageCertificate,
  RevealCeremony,
  Skeleton,
  Switch,
  Textarea,
  isLoadableImageUrl,
  type CertificateShare,
  type PatronageCertificateProps,
  type SealedDossierData,
} from "@greenhold/components";

import { DemoContentNotice } from "../components/DemoContentNotice";
import { useEffectiveMotionMode } from "../features/preferences/useEffectiveMotionMode";
import { certSerialFromShareId } from "../lib/certSerial";
import {
  publicShopUrl,
  certificatePermalinkUrl,
  sealedDossierPermalinkUrl,
} from "../lib/publicSiteUrls";
import { ListForSaleDialog } from "../features/marketplace/ListForSaleDialog";
import {
  useShareTierFloorPrice,
  type ShareTier,
} from "../features/marketplace/useShareTierFloorPrice";
import { useDropForPurchase } from "../features/dossier-purchase/useDropForPurchase";
import { useShareDetail } from "../features/vault/useShareDetail";
import { useShareToggle } from "../features/vault/useShareToggle";
import { usePublishToggle } from "../features/vault/usePublishToggle";
import { useAccountMe } from "../features/admin/useAccountMe";
import { useFlagContent } from "../features/moderation/useFlagContent";
import type { ShareDetailSealedDossier } from "../features/vault/vaultTypes";

/// Humanize the holding DURATION since `opened_at` as an elapsed count that
/// grows the longer the cert is held (it follows a "Holding for" label, so no
/// "ago"): "0 days" the day it's opened → "1 day" → "5 days" → "2 weeks" →
/// "3 months" → "1 year". Pluralized; never negative.
function humanizeHolding(openedAt: string, now: Date = new Date()): string {
  const opened = new Date(openedAt);
  const days = Math.max(
    0,
    Math.floor((now.getTime() - opened.getTime()) / (24 * 60 * 60 * 1000)),
  );
  const unit = (n: number, u: string) => `${n} ${u}${n === 1 ? "" : "s"}`;
  if (days < 14) return unit(days, "day");
  if (days < 60) return unit(Math.floor(days / 7), "week");
  if (days < 365) return unit(Math.floor(days / 30), "month");
  return unit(Math.floor(days / 365), "year");
}

/// Build the SealedDossierData prop <RevealCeremony> needs from the
/// share + sealed_dossier we have in hand. The replay UI doesn't show
/// the retiring-warning, so drop_status="live" is a safe stand-in.
/// Maps the opened share onto <PatronageCertificate> props for the detail
/// hero. Unlike the compact vault thumbnail, the large hero INCLUDES the
/// creator quote (snapshot_hello_text — a real field, not invented; the old
/// <Certificate> rendered it as a blockquote), guarded so an empty string
/// omits the quote element. dossier ← portfolio title, issued ← opened_at
/// (ISO date), serial ← share_serial, portrait ← snapshot image (guarded).
function certPropsFromShareDetail(
  share: CertificateShare,
): PatronageCertificateProps {
  const portraitUrl = isLoadableImageUrl(share.snapshot_image_signed_url)
    ? share.snapshot_image_signed_url
    : null;
  return {
    tier: share.tier,
    name: share.snapshot_display_name,
    serial: certSerialFromShareId(share.share_id),
    dossier: share.portfolio_title,
    issued: share.opened_at.slice(0, 10),
    condition: share.condition,
    signed: share.signed,
    portraitUrl,
    quote: share.snapshot_hello_text || undefined,
  };
}

function dossierFromShare(
  share: CertificateShare,
  _sealedDossier: ShareDetailSealedDossier,
): SealedDossierData {
  return {
    id: share.dossier_id,
    drop_id: share.drop_id,
    edition_number: share.edition_number,
    portfolio_title: share.portfolio_title,
    drop_status: "live",
    retiring_at: null,
    seal_color: share.seal_color ?? null,
  };
}

/// Inject `<meta name="robots" content="noindex">` on mount; remove on
/// unmount. Per PRD §SEO Strategy `/vault/*` is noindex.
function useNoindex() {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex";
    document.head.appendChild(tag);
    return () => {
      document.head.removeChild(tag);
    };
  }, []);
}

export function ShareDetailPage() {
  useNoindex();
  const { share_id } = useParams<{ share_id: string }>();
  const query = useShareDetail(share_id);

  const [replayOpen, setReplayOpen] = useState(false);

  if (query.isLoading) {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-brand-paper-200 text-brand-forest-900">
        <main className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 pb-16 pt-8">
          <Skeleton className="w-48 h-4 rounded-md" />
          <div className="flex flex-col gap-6 rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-5 text-brand-paper-100 shadow-md">
            <Skeleton className="w-full h-[420px] rounded-md" />
            <div className="space-y-2">
              <Skeleton className="w-3/4 h-4 rounded-md" />
              <Skeleton className="w-1/2 h-4 rounded-md" />
              <Skeleton className="w-2/3 h-4 rounded-md" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (query.isError) {
    const is404 = query.error.status === 404;
    const is401 = query.error.status === 401;
    return (
      <div className="min-h-[calc(100vh-3.5rem)] bg-brand-paper-200 text-brand-forest-900">
        <main className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-6 pb-16 pt-8">
          <section className="space-y-3 rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-6 text-brand-paper-100 shadow-md">
            <h1 className="font-heading text-xl font-semibold">
              {is404
                ? "This certificate is no longer available."
                : is401
                  ? "Sign in to view this certificate"
                  : "We couldn't load this certificate"}
            </h1>
            {!is404 && !is401 && (
              <p className="font-body text-sm opacity-80">
                {query.error.message}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/vault">
                <Button variant="primary">Back to Collection</Button>
              </Link>
              {!is404 && (
                <Button variant="secondary" onClick={() => query.refetch()}>
                  Try again
                </Button>
              )}
            </div>
          </section>
        </main>
      </div>
    );
  }

  const { share, sealed_dossier } = query.data!.data;
  const heldDuration = humanizeHolding(share.opened_at);
  const acquiredCurrency = (sealed_dossier.acquired_for_cents / 100).toFixed(2);
  const handleShare = async () => {
    // Share the PUBLIC drop page (reachable by anyone), not this
    // owner-gated detail URL.
    const shareUrl = publicShopUrl(share.drop_id);
    const shareText = `Check out this dossier I opened on @Greenhold — ${share.snapshot_display_name} · ${share.tier} — ${shareUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Greenhold certificate",
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch {
        // user cancelled; fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — try again from your browser.");
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-brand-paper-200 text-brand-forest-900">
    <main className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 pb-16 pt-8">
      <Breadcrumbs share={share} />

      {/* The certificate + its details on a framed forest inset (the artifact
          ground), on the parchment page — matching Collection / Market. */}
      <div className="flex flex-col gap-6 rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-5 text-brand-paper-100 shadow-md">
      <section className="flex justify-center">
        <PatronageCertificate
          width={320}
          {...certPropsFromShareDetail(share)}
        />
      </section>

      {/* Story 4.4 — LIVE OriginLinkFooter (was Story 4.3 placeholder
          span). URL + text content are identical so the upgrade is
          invisible from a URL/SEO perspective; the visual upgrade is
          the golden traceback line micro-animation on hover + the
          framer-motion exit animation on navigation. */}
      <div className="text-center">
        <OriginLinkFooter share={share} variant="prominent" />
      </div>

      <MetaPanel
        portfolioTitle={share.portfolio_title}
        acquiredCurrency={acquiredCurrency}
        heldDuration={heldDuration}
        openedAt={share.opened_at}
        creatorUserId={share.creator_id}
        tier={share.tier as ShareTier}
        signed={share.signed}
      />

      <ActionToolbar
        share={share}
        onShare={handleShare}
        onReplay={() => setReplayOpen(true)}
      />

      <MarketplaceActivityPanel />
      <MarketplaceListingsPanel />

      {/* Story 5.2 — "List for sale" CTA. The share-detail endpoint
          is owner-filtered (404 SHARE_NOT_FOUND otherwise), so reaching
          this page implies ownership; no conditional render needed. */}
      <ListForSaleSection share={share} />

      {/* Story 4.8 — low-emphasis Flag affordance below the action
          toolbar. v1 simplification of the epic AC's "action toolbar
          overflow menu" placement: consistent footer-link pattern
          across all 3 surfaces (creator page footer + bulletin
          footer + this) without needing the unshipped shadcn
          DropdownMenu primitive. */}
      <FlagShareSection share={share} />

      <DemoContentNotice />
      </div>

      {replayOpen && (
        <ReplayCeremonyMount
          share={share}
          dossier={dossierFromShare(share, sealed_dossier)}
          onClose={() => setReplayOpen(false)}
        />
      )}
    </main>
    </div>
  );
}

function Breadcrumbs({ share }: { share: CertificateShare }) {
  return (
    <nav aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-2 text-sm font-body opacity-80">
        <li>
          <Link to="/vault" className="underline">
            Collection
          </Link>
        </li>
        <li aria-hidden="true">›</li>
        <li>
          <a href={publicShopUrl(share.drop_id)} className="underline">
            {share.portfolio_title}
          </a>
        </li>
        <li aria-hidden="true">›</li>
        <li>
          <Link to={`/creators/${share.creator_handle}`} className="underline">
            {share.snapshot_display_name}
          </Link>
          {" "}
          Certificate #{share.share_serial}
        </li>
      </ol>
    </nav>
  );
}

interface MetaPanelProps {
  portfolioTitle: string;
  acquiredCurrency: string;
  heldDuration: string;
  openedAt: string;
  creatorUserId: string;
  tier: ShareTier;
  signed: boolean;
}

function MetaPanel({
  portfolioTitle,
  acquiredCurrency,
  heldDuration,
  openedAt,
  creatorUserId,
  tier,
  signed,
}: MetaPanelProps) {
  const openedFormatted = new Date(openedAt).toLocaleDateString();
  // Current floor = the lowest active ask for a COMPARABLE certificate — same
  // creator, tier AND signed status (a signed founder's and an unsigned common
  // aren't comparable). `null` means no such listings exist yet.
  const floor = useShareTierFloorPrice({
    creatorUserId,
    tier,
    signed,
    enabled: true,
  });
  const floorCents = floor.data?.floor_price_cents ?? null;
  return (
    <section
      aria-label="Certificate metadata"
      className="rounded-lg border border-border bg-card/40 p-4"
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm font-body">
        <div className="flex justify-between">
          <dt className="opacity-70">Drop</dt>
          <dd>{portfolioTitle}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="opacity-70">Bought for</dt>
          <dd>€{acquiredCurrency}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="opacity-70">Holding for</dt>
          <dd>{heldDuration}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="opacity-70">Opened on</dt>
          <dd>{openedFormatted}</dd>
        </div>
        <div className="flex justify-between gap-4 sm:col-span-2">
          <dt className="opacity-70">Current floor price</dt>
          <dd className="text-right">
            {floor.isLoading ? (
              <span className="opacity-60">…</span>
            ) : floor.isError ? (
              <span className="text-xs opacity-60">
                Couldn&apos;t load the floor price
              </span>
            ) : floorCents !== null ? (
              <>€{(floorCents / 100).toFixed(2)}</>
            ) : (
              <span className="text-xs opacity-60">
                No active listings matching this certificate yet
              </span>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}

interface ActionToolbarProps {
  share: CertificateShare;
  onShare: () => void | Promise<void>;
  onReplay: () => void;
}

function ActionToolbar({ share, onShare, onReplay }: ActionToolbarProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4">
        {/* Story 10.2 — publish this certificate to the owner's public
            collection. Distinct from Favourite (private) and from FR39's
            unshipped Highlight (curation) — this is the VISIBILITY axis. */}
        <PublishToggle
          targetKind="share"
          targetId={share.share_id}
          checked={share.is_public_in_collection ?? false}
        />
        {/* Favourite toggle (private). "Highlight on profile" is cut for
            V1 — its toggle + the public-profile highlight surface are
            removed; only the private Favourite remains. */}
        <FlagToggle
          share={share}
          action="favourite"
          label="Favourite"
          helperText="Favourites are private — only you see them in your collection."
          checked={share.is_favourited_by_owner}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={onShare}>
          Share
        </Button>
        <Button
          variant="secondary"
          disabled
          title="List-for-sale ships with Epic 5 marketplace"
        >
          List for sale
        </Button>
        <Button variant="primary" onClick={onReplay}>
          Replay reveal ceremony
        </Button>
      </div>
    </div>
  );
}

interface FlagToggleProps {
  share: CertificateShare;
  action: "highlight" | "favourite";
  label: string;
  helperText: string;
  checked: boolean;
}

function FlagToggle({
  share,
  action,
  label,
  helperText,
  checked,
}: FlagToggleProps) {
  const mutation = useShareToggle({
    shareId: share.share_id,
    action,
  });
  const switchId = `share-flag-${action}`;
  function handleChange(next: boolean) {
    mutation.mutate(
      { state: next },
      {
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  }
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={switchId}
          className="font-body text-sm font-semibold cursor-pointer"
        >
          {label}
        </label>
        <Switch
          id={switchId}
          checked={checked}
          disabled={mutation.isPending}
          onCheckedChange={handleChange}
        />
      </div>
      <p className="text-xs opacity-70 font-body">{helperText}</p>
    </div>
  );
}

interface PublishToggleProps {
  targetKind: "share" | "sealed_dossier";
  targetId: string;
  checked: boolean;
}

// Story 10.2 — the opt-in publish control. Copy states plainly that a
// published item is visible to anyone with the link, including signed-out
// visitors. Not labelled "Highlight" (a different, unshipped concept).
function PublishToggle({ targetKind, targetId, checked }: PublishToggleProps) {
  const mutation = usePublishToggle({ targetKind, targetId });
  const accountMe = useAccountMe();
  const profilePrivate =
    accountMe.data?.data.profile_visibility === "private";
  const switchId = `publish-${targetKind}-${targetId}`;
  function handleChange(next: boolean) {
    mutation.mutate(
      { state: next },
      {
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  }
  async function handleCopyItemLink() {
    const url =
      targetKind === "share"
        ? certificatePermalinkUrl(targetId)
        : sealedDossierPermalinkUrl(targetId);
    if (navigator.share) {
      try {
        await navigator.share({ url });
        return;
      } catch {
        // cancelled; fall through
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — try again from your browser.");
    }
  }
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card/40 p-3">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={switchId}
          className="font-body text-sm font-semibold cursor-pointer"
        >
          Show in public collection
        </label>
        <Switch
          id={switchId}
          checked={checked}
          disabled={mutation.isPending}
          onCheckedChange={handleChange}
        />
      </div>
      <p className="text-xs opacity-70 font-body">
        Visible to anyone with the link, including people who are not signed
        in. Turn off to take it down — it stops showing on your next request.
      </p>
      {/* Story 10.5 — the DURABLE per-item link is /c/ or /d/ (survives a
          handle rename + the 90-day reclaim hazard), never the /u/ handle
          URL. Only offered while published (the link 404s otherwise). */}
      {checked && (
        <button
          type="button"
          onClick={handleCopyItemLink}
          className="self-start rounded text-xs font-semibold underline decoration-dotted"
        >
          Copy link to this {targetKind === "share" ? "certificate" : "dossier"}
        </button>
      )}
      {/* Story 10.5 — publishing never silently flips profile visibility.
          A private profile's /u/ page 404s; the item still opens via its own
          permalink, but say so and point at settings. */}
      {checked && profilePrivate && (
        <p className="text-xs font-body text-state-warning-fg">
          Your profile is private, so your public collection page returns 404.
          This item still opens via its own link.{" "}
          <Link to="/account/profile" className="underline">
            Make your profile public
          </Link>
          .
        </p>
      )}
    </div>
  );
}

function MarketplaceActivityPanel() {
  return (
    <section
      aria-label="Marketplace activity"
      className="rounded-lg border border-border bg-card/40 p-4 space-y-2"
    >
      <h2 className="font-heading text-base font-semibold">
        Marketplace activity
      </h2>
      <p className="text-sm opacity-70">
        Marketplace activity will appear once trading is live.
      </p>
    </section>
  );
}

function MarketplaceListingsPanel() {
  return (
    <section
      aria-label="Current marketplace listings"
      className="rounded-lg border border-border bg-card/40 p-4 space-y-2"
    >
      <h2 className="font-heading text-base font-semibold">
        Current marketplace listings
      </h2>
      <p className="text-sm opacity-70">
        No active listings yet — listings ship with the marketplace (Epic 5).
      </p>
    </section>
  );
}

// Story 5.2 — "List for sale" affordance + ListForSaleDialog mount on
// share-detail. The button is primary (variant="default") because
// listing-for-sale is the principal Epic 5 action this page enables.
// The dialog handles its own price input + fee-breakdown + expiry +
// anonymous toggle + KYC threshold + outcomes; this section is just
// the trigger + mount.
function ListForSaleSection({ share }: { share: CertificateShare }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="flex justify-center pt-2">
      <Button
        type="button"
        variant="primary"
        onClick={() => setOpen(true)}
      >
        List for sale
      </Button>
      <ListForSaleDialog
        open={open}
        onOpenChange={setOpen}
        shareId={share.share_id}
        creatorUserId={share.creator_id}
        tier={share.tier as ShareTier}
        signed={share.signed}
      />
    </section>
  );
}

// Story 4.8 — Flag affordance + AlertDialog for share-detail. Low-
// emphasis text-link trigger below the action toolbar (v1 simplification
// of the epic AC's "action toolbar overflow menu" placement — see Story
// 4.8 spec scope-out #4). useFlagContent owns the POST; toast surfaces
// the 200/429/400 outcomes.
function FlagShareSection({ share }: { share: CertificateShare }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const mutation = useFlagContent();

  const previewLabel = `${share.snapshot_display_name} share #${share.share_serial}`;

  async function handleSubmit() {
    const trimmed = reason.trim();
    try {
      await mutation.mutateAsync({
        target_kind: "share_certificate",
        target_id: share.share_id,
        reason: trimmed === "" ? undefined : trimmed,
      });
      toast.success("Thanks. We'll review this within 48 hours.");
      setOpen(false);
      setReason("");
    } catch (err) {
      const detail = err as { code?: string; message?: string };
      if (detail?.code === "RATE_LIMITED") {
        toast.error("You've reached the daily flagging limit. Try again later.");
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error("Flag submission failed. Try again in a moment.");
      }
    }
  }

  return (
    <section className="text-center pt-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs opacity-70 underline decoration-dotted underline-offset-2 hover:opacity-100 hover:decoration-solid"
        aria-haspopup="dialog"
      >
        Flag this content
      </button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Flag {previewLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              Tell us why this content is inappropriate. Reason is optional
              but encouraged. We'll review within 48 hours.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional reason (max 500 characters)..."
            maxCharacters={500}
            rows={4}
            disabled={mutation.isPending}
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={mutation.isPending}
              onClick={() => {
                setReason("");
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={mutation.isPending}
              onClick={(e) => {
                // Prevent the AlertDialog auto-close so we control
                // close-on-success only (the mutation might fail and
                // we want to keep the dialog open with the typed
                // textarea preserved).
                e.preventDefault();
                void handleSubmit();
              }}
            >
              {mutation.isPending ? "Submitting…" : "Submit flag"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/// Inner mount so useEffectiveCeremonyMode (which calls hooks) is only
/// invoked once the share data is available. Mirrors WelcomePage /
/// VaultPage's RevealCeremonyMount pattern.
function ReplayCeremonyMount({
  share,
  dossier,
  onClose,
}: {
  share: CertificateShare;
  dossier: SealedDossierData;
  onClose: () => void;
}) {
  const reducedMotion = useEffectiveMotionMode() === "reduced-motion";
  const drop = useDropForPurchase(share.drop_id);
  const creators = useMemo(
    () =>
      (drop.data?.creators ?? []).map((c) => ({
        name: c.display_name || (c.handle ? `@${c.handle}` : ""),
        img: c.image_signed_url,
      })),
    [drop.data],
  );
  return (
    <RevealCeremony
      open
      dossier={dossier}
      share={share}
      certificate={
        <PatronageCertificate {...certPropsFromShareDetail(share)} />
      }
      serial={certSerialFromShareId(share.share_id)}
      theme={drop.data?.portfolio_theme ?? null}
      creators={creators}
      reducedMotion={reducedMotion}
      onClose={onClose}
    />
  );
}
